/**
 * Create Meal Plan Page
 *
 * Page component for creating new meal plans. Uses the Layout component
 * and integrates the CreateMealPlanForm component with meal plan creation logic.
 *
 * @module components/pages/CreateMealPlanPage
 */

import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { CreateMealPlanForm } from '@/components/meal-plans';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { LoadingDialog } from '@/components/ui/LoadingDialog';
import { useCreateMealPlan } from '@/hooks/useCreateMealPlan';
import { BackButton } from '../ui/BackButton';

/**
 * Create Meal Plan Page Component
 */
export const CreateMealPlanPage: React.FC = () => {
  const { isSubmitting, error, handleSubmit, clearError, loadingProgress } = useCreateMealPlan();

  return (
    <>
      <Layout
        showHeader={true}
        showFooter={true}
      >
        <div className="max-w-2xl mx-auto">
          <div className="my-8">
            <div className="flex items-center gap-4 mb-4">
              <BackButton fallback='/meal-plans' />
            </div>

            <TitleText className="mb-2">Create New Meal Plan</TitleText>
            <BodyText variant="muted">
              Configure your personalised meal plan parameters
            </BodyText>
          </div>
          <CreateMealPlanForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={error}
            onClearError={clearError}
          />
        </div>
      </Layout>

      {/* Loading Dialog */}
      <LoadingDialog
        isOpen={loadingProgress.isLoading}
        title={loadingProgress.loadingText}
        message="Creating your personalised meal plan..."
        estimatedTimeSeconds={loadingProgress.estimatedTimeSeconds}
        isComplete={loadingProgress.progress === 100}
      />
    </>
  );
};

export default CreateMealPlanPage;